import React, { useState, useEffect } from 'react';
import api from '../../utils/api';
import toast from 'react-hot-toast';

export default function SubscriptionManager() {
  const [sub, setSub]     = useState(null);
  const [plans, setPlans] = useState([]);
  const [usage, setUsage] = useState(null);
  const [loading, setLoading] = useState(true);
  const [cycle, setCycle]     = useState('monthly');

  useEffect(() => {
    Promise.all([
      api.get('/tenant/subscription'),
      api.get('/plans'),
      api.get('/tenant/subscription/usage'),
    ]).then(([s, p, u]) => { setSub(s.data); setPlans(p.data); setUsage(u.data); })
    .catch(err => toast.error(err.response?.data?.error || 'Failed to load'))
    .finally(() => setLoading(false));
  }, []);

  const upgrade = async (planId) => {
    try {
      const r = await api.put('/tenant/subscription/plan', { plan_id: planId, billing_cycle: cycle });
      setSub(r.data);
      toast.success('Plan updated!');
    } catch (err) { toast.error(err.response?.data?.error || 'Failed'); }
  };

  const planColor = (slug) => ({ free: '#a6e3a1', starter: '#89b4fa', professional: '#fab387', enterprise: '#f38ba8' }[slug] || '#cdd6f4');

  if (loading) return <div style={{ color: 'var(--text-3)', padding: 40, textAlign: 'center' }}>Loading…</div>;

  return (
    <div>
      {/* Current subscription */}
      {sub && (
        <div style={{ background: 'var(--surface)', border: `1px solid ${planColor(sub.plan_id?.replace('plan_',''))}44`, borderRadius: 12, padding: 20, marginBottom: 24 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: 12 }}>
            <div>
              <div style={{ fontSize: 12, color: 'var(--text-3)', marginBottom: 4 }}>CURRENT PLAN</div>
              <div style={{ fontSize: 20, fontWeight: 700, marginBottom: 4 }}>{sub.plan_name}</div>
              <div style={{ fontSize: 13, color: 'var(--text-2)' }}>{sub.status} · {sub.billing_cycle}
                {sub.current_period_end && ` · Renews ${new Date(sub.current_period_end).toLocaleDateString()}`}
              </div>
            </div>
            <div style={{ textAlign: 'right' }}>
              <div style={{ fontSize: 24, fontWeight: 700 }}>${sub.amount}<span style={{ fontSize: 13, color: 'var(--text-3)' }}>/{sub.billing_cycle === 'yearly' ? 'yr' : 'mo'}</span></div>
            </div>
          </div>
        </div>
      )}

      {/* Usage meters */}
      {usage && (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill,minmax(200px,1fr))', gap: 12, marginBottom: 24 }}>
          {[
            { label: 'Members',     val: usage.member_count,    max: usage.limits?.max_members,      unit: '' },
            { label: 'Projects',    val: usage.project_count,   max: usage.limits?.max_projects,     unit: '' },
            { label: 'API Keys',    val: usage.api_key_count,   max: usage.limits?.max_api_keys,     unit: '' },
            { label: 'API Calls',   val: usage.calls_this_month,max: usage.limits?.max_monthly_calls,unit: '' },
          ].map(m => {
            const pct  = m.max > 0 ? Math.min((m.val / m.max) * 100, 100) : 0;
            const over = m.max > 0 && m.val >= m.max;
            return (
              <div key={m.label} style={{ background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 12, padding: 16 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 13, marginBottom: 8 }}>
                  <span style={{ color: 'var(--text-2)' }}>{m.label}</span>
                  <span style={{ fontWeight: 600, color: over ? '#f38ba8' : undefined }}>{m.val?.toLocaleString()}{m.max > 0 ? ` / ${m.max === -1 ? '∞' : m.max.toLocaleString()}` : ''}</span>
                </div>
                {m.max > 0 && m.max !== -1 && (
                  <div style={{ height: 4, background: 'var(--border)', borderRadius: 4 }}>
                    <div style={{ height: '100%', width: `${pct}%`, background: over ? '#f38ba8' : 'var(--accent)', borderRadius: 4, transition: 'width .3s' }} />
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* Plan picker */}
      <h3 style={{ fontSize: 14, fontWeight: 600, marginBottom: 12 }}>Upgrade Plan</h3>
      <div style={{ display: 'flex', gap: 8, marginBottom: 20 }}>
        {['monthly','yearly'].map(c => (
          <button key={c} onClick={() => setCycle(c)}
            style={{ padding: '6px 14px', borderRadius: 8, border: '1px solid var(--border)', background: cycle === c ? 'var(--accent)' : 'transparent', color: cycle === c ? '#fff' : 'var(--text-2)', cursor: 'pointer', fontSize: 13 }}>
            {c}{c === 'yearly' && ' (Save 20%)'}
          </button>
        ))}
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill,minmax(220px,1fr))', gap: 16 }}>
        {plans.map(p => {
          const price  = cycle === 'yearly' ? p.price_yearly : p.price_monthly;
          const isActive = sub?.plan_id === p.id;
          const color  = planColor(p.slug);
          return (
            <div key={p.id} style={{ background: 'var(--surface)', border: `1px solid ${isActive ? color : 'var(--border)'}`, borderRadius: 12, padding: 20 }}>
              <div style={{ fontSize: 12, fontWeight: 700, color, marginBottom: 8, letterSpacing: 1 }}>{p.name.toUpperCase()}</div>
              <div style={{ fontSize: 22, fontWeight: 700, marginBottom: 16 }}>${price}<span style={{ fontSize: 12, color: 'var(--text-3)' }}>/{cycle === 'yearly' ? 'yr' : 'mo'}</span></div>
              <div style={{ fontSize: 12, color: 'var(--text-2)', marginBottom: 16, lineHeight: 1.6 }}>
                {[
                  `${p.max_members === -1 ? 'Unlimited' : p.max_members} members`,
                  `${p.max_projects === -1 ? 'Unlimited' : p.max_projects} projects`,
                  `${p.max_api_keys === -1 ? 'Unlimited' : p.max_api_keys} API keys`,
                  `${p.max_monthly_calls === -1 ? 'Unlimited' : (p.max_monthly_calls/1000)+'k'} API calls/mo`,
                ].map(f => <div key={f}>✓ {f}</div>)}
              </div>
              <button onClick={() => !isActive && upgrade(p.id)} disabled={isActive}
                style={{ width: '100%', padding: '8px 0', borderRadius: 8, background: isActive ? 'transparent' : color, color: isActive ? 'var(--text-3)' : '#fff', border: `1px solid ${isActive ? 'var(--border)' : color}`, cursor: isActive ? 'default' : 'pointer', fontSize: 13, fontWeight: 600 }}>
                {isActive ? 'Current plan' : 'Upgrade'}
              </button>
            </div>
          );
        })}
      </div>
    </div>
  );
}
